"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Headphones, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface PropertyPodcastProps {
  podcastUrl: string;
  className?: string;
}

// Move waveform generation outside component to prevent regeneration
function generateWaveformBars(count: number) {
  return Array.from({ length: count }, (_, i) => {
    // Create more varied heights using multiple sine waves
    const x = i / count;
    const wave1 = Math.sin(x * Math.PI * 4);
    const wave2 = Math.sin(x * Math.PI * 7) * 0.5;
    const wave3 = Math.sin(x * Math.PI * 2) * 0.25;

    // Combine waves and normalize to desired height range (4px to 20px)
    return Math.abs((wave1 + wave2 + wave3) * 8 + 12);
  });
}

const WAVEFORM_BARS = generateWaveformBars(32);

export function PropertyPodcast({
  podcastUrl,
  className,
}: PropertyPodcastProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const togglePlay = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const currentProgress =
        (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleProgressChange = useCallback((percentage: number) => {
    if (audioRef.current) {
      const time = (percentage / 100) * audioRef.current.duration;
      audioRef.current.currentTime = time;
      setProgress(percentage);
    }
  }, []);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      handleProgressChange(percentage);
    },
    [handleProgressChange],
  );

  const handleProgressKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const step = e.key === "ArrowLeft" ? -1 : 1;
        handleProgressChange(Math.max(0, Math.min(100, progress + step)));
      }
    },
    [progress, handleProgressChange],
  );

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;

      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", () => setIsPlaying(false));

      return () => {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", () => setIsPlaying(false));
      };
    }
  }, [handleTimeUpdate, handleLoadedMetadata]);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  // Memoize the waveform to prevent unnecessary re-renders
  const waveform = useMemo(
    () => (
      <div className="flex items-center gap-1.5">
        {WAVEFORM_BARS.map((height, i) => {
          const barProgress = (i / WAVEFORM_BARS.length) * 100;
          const barWidth = 100 / WAVEFORM_BARS.length;
          const startX = barProgress;
          const endX = startX + barWidth;

          // Calculate how much this specific bar should be filled
          const fillPercentage = Math.max(
            0,
            Math.min(
              100,
              progress <= startX
                ? 0
                : progress >= endX
                  ? 100
                  : ((progress - startX) / barWidth) * 100,
            ),
          );

          return (
            <div
              key={`waveform-${i}-${height}`}
              className="flex-1 rounded-full relative"
              style={{ height: `${height}px` }}
            >
              {/* Background bar */}
              <div
                className={cn(
                  "absolute inset-0 rounded-full transition-colors duration-300",
                  "bg-gray-100",
                  isHovered && "opacity-80",
                )}
              />

              {/* Foreground bar with gradient mask */}
              <div
                className={cn(
                  "absolute inset-0 rounded-full transition-all duration-300",
                  isPlaying ? "bg-primary" : "bg-gray-900",
                )}
                style={{
                  clipPath: `inset(0 ${100 - fillPercentage}% 0 0)`,
                  transition: "clip-path 0.1s ease-out",
                }}
              />
            </div>
          );
        })}
      </div>
    ),
    [progress, isPlaying, isHovered],
  );

  return (
    <Card
      className={cn(
        "relative overflow-hidden bg-white transition-shadow duration-300",
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Content */}
      <div className="relative p-3 sm:p-4 md:p-6">
        <div className="flex items-start gap-2 sm:gap-4 md:gap-6">
          {/* Play Button Column */}
          <div className="flex flex-col items-center gap-2">
            <Button
              variant={isPlaying ? "default" : "outline"}
              size="icon"
              className={cn(
                "h-10 w-10 sm:h-12 sm:w-12 rounded-full transition-all duration-300",
                isPlaying ? "shadow-md" : "hover:scale-105",
              )}
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <Play className="h-5 w-5 sm:h-6 sm:w-6 ml-0.5 sm:ml-1" />
              )}
            </Button>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium hidden sm:block">
              {isPlaying ? "Playing" : "Listen"}
            </span>
          </div>

          {/* Main Content Column */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Headphones className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-medium">AI Property Analysis</h3>
              <span className="text-xs text-muted-foreground">
                {formatTime(duration)}
              </span>
            </div>

            <div className="relative h-6 sm:h-8 group">
              {waveform}

              {/* Click Handler */}
              <div
                role="slider"
                tabIndex={0}
                aria-label="Audio progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                className="absolute inset-0 cursor-pointer"
                onClick={handleProgressClick}
                onKeyDown={handleProgressKeyDown}
              />
            </div>

            {/* Time and Volume Controls */}
            <div className="flex items-center justify-between mt-1 sm:mt-2">
              <span className="text-[10px] sm:text-xs text-muted-foreground">
                {formatTime(duration * (progress / 100))} /{" "}
                {formatTime(duration)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 sm:h-8 sm:w-8"
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <VolumeX className="h-3 w-3 sm:h-4 sm:w-4" />
                ) : (
                  <Volume2 className="h-3 w-3 sm:h-4 sm:w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <audio ref={audioRef} src={podcastUrl} preload="metadata">
        <track kind="captions" src="" label="English" />
      </audio>
    </Card>
  );
}
